<script setup lang="ts">
import CustomInput from "@/views/components/CustomInput.vue";
import CustomPhoneInput from "@/views/components/CustomPhoneInput.vue";
import CustomChecker from "@/views/components/CustomChecker.vue";
import savEmailShema from "@/validation/savEmailSchema";
import { contactSav, verifyClientStatus } from "@/services/modules/savService";
import { getCookie, getUserFromLocalStorage, isAuth } from "@/utils/functions";
import { reactive, ref, onMounted, Ref } from "vue";
// @ts-ignore
import i18n from '@/localization/i18n';
import { useAlertsStore } from "@/stores/alerts";
import VueTurnstile from 'vue-turnstile';

// Props
const props = withDefaults(
  defineProps<{
    id: string;
    title?: string;
    titleClasses?: any[];
    paragraph?: string;
    paragraphClasses?: any[];
    hideEmail?: boolean;
    hidePhone?: boolean;
    hideTypeOfRequest?: boolean;
    hideUserType?: boolean;
    hideConsent?: boolean;
    hideName?: boolean;
    hideSubject?: boolean;
    source: string;
    subject?: string;
    style?: any;
    btnStyle?: string;
    message?: string;
    classes?: any[];
    btnContainerClasses?: any[];
    btnClasses: any[];
    btnLabel: string;
    intro: string;
    outro: string;
    messageLabel?: string;
    proPlatformCode?: string | null;
    deactivateAccount?: boolean;
    messageInputRows?: number;
    isForContactPage?: boolean;
  }>(),
  {
    proPlatformCode: null,
    deactivateAccount: false,

    hideConsent: true,
    hidePhone: true,
    hideTypeOfRequest: true,
    hideUserType: true,

    messageInputRows: 7,
    isForContactPage: false,
  }
);

// Local variables
const t = i18n.global.t;
const user = getUserFromLocalStorage() ?? null
// @ts-ignore
const VITE_ASSET_URL = import.meta.env.VITE_ASSET_URL;
// @ts-ignore
const VITE_TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

// Type of request options
const typeOfRequestOptions = [
  t('w_sav_request_type_technical_support'),
  t('w_sav_request_type_usage_question'),
  t('w_sav_request_type_account_subscription'),
  t('w_sav_request_type_billing_compliance'),
  t('w_sav_request_type_commercial_demo'),
  t('w_sav_request_type_partnership_media'),
  t('w_sav_request_type_report_dispute'),
  t('w_sav_request_type_other')
];

// Stores
const alerts = useAlertsStore();

// Refs
const loading = ref(false);
const sendSuccess = ref(false);
const savModalTrigger: Ref<HTMLElement | null> = ref(null);
const deactivateAccount = ref(false)
const canDeactivateAccount = ref(true)

// Reactive variables
const savEmailFormData = reactive({
  email: "",
  name: "",
  phone: "",
  subject: "",
  message: "",
  typeOfRequest: "",
  userType: "",
  consent: false,
  cloud_flare_token: "",
});

const savEmailErrors = reactive({
  email: "",
  name: "",
  phone: "",
  subject: "",
  message: "",
  typeOfRequest: "",
  userType: "",
  consent: "",
  cloud_flare_token: "",
});

// Lifecycle hooks
onMounted(() => {
  if (props.deactivateAccount !== undefined) {
    deactivateAccount.value = props.deactivateAccount
    verifyClient()
  }
  if (isAuth()) {
    savEmailFormData.email = user?.email??'';
    // savEmailFormData.email = 'topela.testing@yopmail.com'
    if (user?.first_name) {
      savEmailFormData.name = user.first_name + " " + user.last_name;
    } else {
      savEmailFormData.name = user?.last_name??'';
    }

    if (props.subject) {
      savEmailFormData.subject = props.subject;
    }

    if (props.message) {
      savEmailFormData.message = props.message;
    }
  }
});

const verifyClient = () => {
  const cookieToken = getCookie("topela_token")
  let account_id = user !== null ? user.party_id : null
  // let token = user !== null ? user.token.access : null
  let token =  cookieToken?.access ?? null
  if (account_id !== null) {
    verifyClientStatus(account_id, token)
      .then((response) => {
        if (response.data.status === "success") {
          canDeactivateAccount.value = true
        } else {
          canDeactivateAccount.value = false
        }
      })
      .catch(() => {});
  }

};

const validateContactPageFields = () => {
  if (!props.isForContactPage) return true;

  let hasError = false;

  if (!savEmailFormData.typeOfRequest) {
    savEmailErrors.typeOfRequest = t('w_msg_frmVal_required');
    hasError = true;
  }

  if (!savEmailFormData.userType) {
    savEmailErrors.userType = t('w_msg_frmVal_required');
    hasError = true;
  }

  if (!savEmailFormData.consent) {
    savEmailErrors.consent = t('w_msg_frmVal_required');
    hasError = true;
  }

  return !hasError;
};

const onSubmitMessage = () => {
  (savEmailErrors.email = ""),
    (savEmailErrors.name = ""),
    (savEmailErrors.phone = ""),
    (savEmailErrors.subject = ""),
    (savEmailErrors.message = ""),
    (savEmailErrors.typeOfRequest = ""),
    (savEmailErrors.userType = ""),
    (savEmailErrors.consent = ""),
    (savEmailErrors.cloud_flare_token = "");

  // Validate contact page specific fields
  if (!validateContactPageFields()) {
    return;
  }

  savEmailShema
    .validate(savEmailFormData, { abortEarly: false })
    .then(() => {
      loading.value = true;
      alerts.clearAlerts()

      // construct new field content in a string with break lines to be appended to message , only if the field content is not empty
      let additionalInfo = "";
      if (savEmailFormData.typeOfRequest) {
        additionalInfo += `Type de demande: ${savEmailFormData.typeOfRequest}\n`;
      }
      if (savEmailFormData.userType) {
        additionalInfo += `Type d'utilisateur: ${savEmailFormData.userType}\n`;
      }
      if (savEmailFormData.phone) {
        additionalInfo += `Téléphone: ${savEmailFormData.phone}\n`;
      }

      // Construct final message with additional info
      const finalMessage = additionalInfo
        ? savEmailFormData.message + "\n" + additionalInfo
        : savEmailFormData.message;

      let payload;
      if (props.proPlatformCode) {
        payload = {
          account_id: isAuth() ? user?.account_id : null,
          email: savEmailFormData.email,
          last_name: savEmailFormData.name,
          message: finalMessage,
          source: props.source,
          subject: savEmailFormData.subject,
          intro: props.intro,
          outro: props.outro,
          pro_platform_code: props.proPlatformCode,
          cloud_flare_token: savEmailFormData.cloud_flare_token,
        };
      } else {
        payload = {
          account_id: isAuth() ? user?.account_id : null,
          email: savEmailFormData.email,
          last_name: savEmailFormData.name,
          message: finalMessage,
          source: props.source,
          subject: savEmailFormData.subject,
          intro: props.intro,
          outro: props.outro,
          cloud_flare_token: savEmailFormData.cloud_flare_token,
        };
      }

      contactSav(payload)
        .then(() => {
          loading.value = false;
          sendSuccess.value = true;
          // @ts-ignore
          window.dataLayer.push({
            event: "formSubmission",
            form_name: 'Client Contact Form',
            form_category: 'Support',
            form_data: {
              email: savEmailFormData.email,
              name: savEmailFormData.name,
              subject: savEmailFormData.subject,
            }
          });
        })
        .catch(() => {
          alerts.putAlert({ code: "message_send_fail", scope: "general" });
          loading.value = false;
        });
    })
    .catch((error) => {
      error.inner.forEach((e: { path: string; message: string }) => {
        // @ts-ignore
        savEmailErrors[e.path] = e.message;
      });
    });
};
</script>

<template>
  <form
    v-if="!sendSuccess"
    class="text-start"
    :class="classes"
    @submit.prevent="onSubmitMessage"
    id="contactForm"
  >
    <h4 v-if="title" :class="titleClasses">{{ title }}</h4>
    <div v-if="deactivateAccount && !canDeactivateAccount" class="alert p-2 alert-danger" role="alert">
      <h6 class="text-danger">{{ t("w_txt_csProfillDeactivateAccount_attention") }} </h6>
      {{ t("w_txt_csProfillDeactivateAccount_client_can_not_be_deactivate") }}
    </div>
    <p v-if="paragraph" :class="paragraphClasses" class="mb-3">
      {{ paragraph }}
    </p>
    <div class="row" v-show="!props.hideTypeOfRequest">
      <div class="col-md-12 mb-1">
        <label class="form-label" for="sav_type_of_request">{{ t('w_sav_request_type_label') }}</label>
        <select
          id="sav_type_of_request"
          v-model="savEmailFormData.typeOfRequest"
          class="select form-select mb-0 rounded"
        >
          <option value="" disabled selected>{{ t('w_sav_request_type_placeholder') }}</option>
          <option v-for="option in typeOfRequestOptions" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
        <span v-if="savEmailErrors.typeOfRequest" class="text-danger text-start">
          {{ savEmailErrors.typeOfRequest }}
        </span>
      </div>
    </div>
    <div class="row" v-show="!props.hideUserType">
      <div class="col-md-12 mb-1">
        <label class="form-label">{{ t('w_sav_user_type_label') }}</label>
        <div class="d-flex gap-2 flex-wrap">
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="userType"
              id="userTypeIndividual"
              :value="t('w_sav_user_type_individual')"
              v-model="savEmailFormData.userType"
            />
            <label class="form-check-label" for="userTypeIndividual">
              {{ t('w_sav_user_type_individual') }}
            </label>
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="userType"
              id="userTypeProfessional"
              :value="t('w_sav_user_type_professional')"
              v-model="savEmailFormData.userType"
            />
            <label class="form-check-label" for="userTypeProfessional">
              {{ t('w_sav_user_type_professional') }}
            </label>
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="userType"
              id="userTypePartner"
              :value="t('w_sav_user_type_partner')"
              v-model="savEmailFormData.userType"
            />
            <label class="form-check-label" for="userTypePartner">
              {{ t('w_sav_user_type_partner') }}
            </label>
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="userType"
              id="userTypeOther"
              :value="t('w_sav_user_type_other')"
              v-model="savEmailFormData.userType"
            />
            <label class="form-check-label" for="userTypeOther">
              {{ t('w_sav_user_type_other') }}
            </label>
          </div>
        </div>
        <span v-if="savEmailErrors.userType" class="text-danger text-start">
          {{ savEmailErrors.userType }}
        </span>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12 mb-1" v-show="!props.hideName">
        <CustomInput
          id="sav_name"
          v-model:value="savEmailFormData.name"
          :disabled="isAuth()"
          :placeholder="t('w_frm_plName')"
          :errors="savEmailErrors.name"
          :classes="['text-start', 'rounded', 'mb-0']"
          :input-label="t('w_sav_full_name_label')"
        />
      </div>
      <div class="col-md-12 mb-1" v-show="!props.hideEmail">
        <CustomInput
          id="sav_email"
          v-model:value="savEmailFormData.email"
          :disabled="isAuth()"
          :placeholder="t('w_frm_plEmail')"
          :errors="savEmailErrors.email"
          :classes="['text-start', 'rounded', 'mb-0']"
          :input-label="t('w_sav_email_label')"
        />
      </div>
      <div class="col-md-12 mb-1" v-show="!props.hidePhone">
        <CustomPhoneInput
          type="text"
          id="sav_phone"
          maxlength="10"
          v-model:value="savEmailFormData.phone"
          :placeholder="t('g_frm_phone')"
          :errors="savEmailErrors.phone"
          :input-label="t('w_sav_phone_label')"
        />
      </div>
      <div class="col-md-12 mb-1" v-show="!props.hideSubject">
        <CustomInput
          id="sav_subject"
          v-model:value="savEmailFormData.subject"
          :placeholder="t('g_frm_plSubject')"
          :errors="savEmailErrors.subject"
          :classes="['text-start', 'rounded', 'mb-0']"
          :input-label="t('w_sav_subject_label')"
        />
      </div>
      <div class="col-md-12 mb-0">
        <div class="form-group text-start mb-1">
          <label v-if="props.messageLabel" for="message" class="form-label">{{
            props.messageLabel
          }}</label>
          <textarea
            class="form-control rounded mb-0"
            v-model="savEmailFormData.message"
            id="message"
            :placeholder="t('g_frm_plMessage')"
            :rows="props.messageInputRows"
          ></textarea>
          <span
            v-if="savEmailErrors.message"
            class="text-danger text-start"
            >{{ savEmailErrors.message }}</span
          >
        </div>
      </div>

      <div class="col-md-12 mb-2" v-show="!props.hideConsent">
        <div class="d-flex flex-row align-items-center">
          <div class="form-check form-switch form-check-primary" style="min-width: auto; padding-left: 0; margin-right: 15px;">
            <CustomChecker
              :input-label="''"
              id="sav_consent"
              v-model:value="savEmailFormData.consent"
              style="margin-left: 0;"
            />
          </div>
          <label class="form-check-label" for="sav_consent" style="font-size: 0.9rem;">
            {{ t('w_sav_consent_label') }}
          </label>
        </div>
        <span v-if="savEmailErrors.consent" class="text-danger text-start">
          {{ savEmailErrors.consent }}
        </span>
      </div>

      <div class="col-md-12 mb-3">
        <div class="d-flex justify-content-center">
          <VueTurnstile
            v-model="savEmailFormData.cloud_flare_token"
            :site-key="VITE_TURNSTILE_SITE_KEY"
            :tabindex="0"
            style="display: flex; justify-content: center; align-items: center; flex-direction: column;"
          />
        </div>
        <span v-if="savEmailErrors.cloud_flare_token" class="text-danger text-start d-block text-center">
          {{ savEmailErrors.cloud_flare_token }}
        </span>
      </div>

      <div class="col-md-12 mb-1">
        <slot></slot>
        <div :class="btnContainerClasses" class="text-start">
          <slot name="cancelButton"></slot>
          <button class="btn on-action-btn btn-lg" :class="btnClasses" :style="btnStyle" id="submit" type="submit">
            <span v-if="!loading">{{ btnLabel }}</span>
            <div
              v-else
              class="spinner-border spinner-border-sm text-light"
              role="status"
            >
              <span class="sr-only">Loading...</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  </form>
  <div v-else class="text-center">
    <div>
      <img
        class="mb-3"
        :src="
          VITE_ASSET_URL +
          'assets/public/assets/img/undraw_Mail_sent_re_0ofv.webp'
        "
        alt=""
        height="150"
      />
    </div>
    <h5 class="px-md-3" v-html="t('w_msg_messageSent')"></h5>
  </div>

  <button
    v-show="false"
    ref="savModalTrigger"
    data-bs-toggle="modal"
    :data-bs-target="`#${id}`"
  ></button>
</template>

<style scoped>
    @import url('@/assets/css/bootstrap-extended2.css');
    @import url('@/assets/css/bootstrap-extended.css');
.form-check-input:checked {
  background-color: #FEB23C;
  border-color: #FEB23C;
}

.form-control:focus,
.form-select:focus {
  border-color: #FEB23C;
  /* box-shadow: 0 0 0 0.02rem #FEB23C; */
  /* padding-left: 1.5rem; */
}

.form-select {
  padding: 0.8rem 1rem !important;
  line-height: 1.6rem;
}

.btn-lg{
  padding: 1rem 4rem 1rem 4rem !important;
}

textarea::-webkit-input-placeholder, textarea::-moz-placeholder, textarea:-ms-input-placeholder {
    font-size: larger;
}
</style>
